import { AlertTriangle, Key, FileCode, Database, Shield, Bug, Lock, Eye, Server, Workflow } from "lucide-react";

export const SecureCodingOwaspTop10 = () => {
  const owaspItems = [
    {
      rank: "A01",
      title: "Broken Access Control",
      description: "Users can act outside their intended permissions. Access control enforces policy so users cannot act outside of their intended permissions.",
      icon: Lock,
      prevention: [
        "Deny by default except for public resources",
        "Implement access control mechanisms once and reuse",
        "Enforce record ownership instead of accepting user input",
        "Log access control failures and alert admins",
      ],
      color: "text-destructive",
    },
    {
      rank: "A02",
      title: "Cryptographic Failures",
      description: "Previously 'Sensitive Data Exposure.' Focus on failures related to cryptography which often lead to sensitive data exposure.",
      icon: Key,
      prevention: [
        "Don't store sensitive data unnecessarily",
        "Encrypt all sensitive data at rest",
        "Use strong, up-to-date algorithms and protocols",
        "Disable caching for sensitive data responses",
      ],
      color: "text-chart-1",
    },
    {
      rank: "A03",
      title: "Injection",
      description: "SQL, NoSQL, OS, LDAP injection occurs when untrusted data is sent to an interpreter as part of a command or query.",
      icon: Database,
      prevention: [
        "Use parameterized queries or prepared statements",
        "Use positive server-side input validation",
        "Escape special characters for residual dynamic queries",
        "Use LIMIT and other SQL controls to prevent mass disclosure",
      ],
      color: "text-chart-2",
    },
    {
      rank: "A04",
      title: "Insecure Design",
      description: "A new category focusing on risks related to design flaws. Secure design requires threat modeling and secure design patterns.",
      icon: FileCode,
      prevention: [
        "Establish secure development lifecycle with AppSec",
        "Use threat modeling for critical authentication and access control",
        "Write unit and integration tests for security controls",
        "Limit resource consumption by user or service",
      ],
      color: "text-chart-4",
    },
    {
      rank: "A05",
      title: "Security Misconfiguration",
      description: "Missing security hardening, improperly configured permissions, unnecessary features enabled, default accounts unchanged.",
      icon: Server,
      prevention: [
        "Repeatable hardening process for fast, locked-down environments",
        "Minimal platform without unnecessary features or components",
        "Review and update configurations for all security notes and patches",
        "Automated verification of configurations in all environments",
      ],
      color: "text-primary",
    },
    {
      rank: "A06",
      title: "Vulnerable Components",
      description: "Using components with known vulnerabilities. You're vulnerable if you don't know versions of all components you use.",
      icon: Bug,
      prevention: [
        "Remove unused dependencies and unnecessary features",
        "Continuously inventory component versions (npm audit, Snyk)",
        "Only obtain components from official sources over secure links",
        "Monitor for unmaintained libraries without security patches",
      ],
      color: "text-chart-3",
    },
    {
      rank: "A07",
      title: "Authentication Failures",
      description: "Confirmation of user identity, authentication, and session management is critical to protect against authentication-related attacks.",
      icon: Shield,
      prevention: [
        "Implement multi-factor authentication",
        "Don't deploy with default credentials",
        "Implement weak password checks against top 10k passwords",
        "Limit or delay failed login attempts (rate limiting)",
      ],
      color: "text-destructive",
    },
    {
      rank: "A08",
      title: "Software & Data Integrity",
      description: "Code and infrastructure that doesn't protect against integrity violations. Insecure CI/CD pipelines can introduce unauthorized access.",
      icon: Workflow,
      prevention: [
        "Use digital signatures to verify software/data from expected source",
        "Ensure libraries and dependencies are from trusted repositories",
        "Use software supply chain security tools (OWASP Dependency Check)",
        "Review code and configuration changes to minimize malicious code",
      ],
      color: "text-chart-1",
    },
    {
      rank: "A09",
      title: "Security Logging & Monitoring",
      description: "Without logging and monitoring, breaches cannot be detected. Insufficient logging, detection, monitoring, and active response.",
      icon: Eye,
      prevention: [
        "Log all login, access control, and server-side validation failures",
        "Ensure logs are in a format easily consumed by log management",
        "Encode log data correctly to prevent injections or attacks",
        "Establish effective monitoring and alerting for suspicious activity",
      ],
      color: "text-chart-2",
    },
    {
      rank: "A10",
      title: "Server-Side Request Forgery",
      description: "SSRF flaws occur when a web application fetches a remote resource without validating the user-supplied URL.",
      icon: AlertTriangle,
      prevention: [
        "Sanitize and validate all client-supplied input data",
        "Enforce URL schema, port, and destination with allow list",
        "Disable HTTP redirections",
        "Don't send raw responses to clients",
      ],
      color: "text-chart-4",
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        OWASP Top 10 (2021) — Complete Guide
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        The OWASP Top 10 is the definitive list of the most critical security risks to web applications. 
        Every developer should know these by heart.
      </p>

      <div className="space-y-6">
        {owaspItems.map((item, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 hover:border-primary/30 transition-colors"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0`}>
                  <span className={`text-sm font-bold ${item.color}`}>{item.rank}</span>
                </div>
                <div className="lg:hidden">
                  <h3 className="font-semibold text-foreground">{item.title}</h3>
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="hidden lg:block font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Prevention:</h4>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {item.prevention.map((tip, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-0.5">✓</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
